import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { verifyEmail, resendOtp, clearError, clearSuccessMessage, setPendingVerificationEmail } from "../../redux/slices/authSlice";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Paper, Alert, CircularProgress, IconButton, TextField, Button } from "@mui/material";
import ArrowBackIosOutlined from "@mui/icons-material/ArrowBackIosOutlined";

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, successMessage, isAuthenticated, isNewUser, pendingVerificationEmail } = useSelector((state) => state.auth);

  const [otp, setOtp] = useState("");
  const email = location.state?.email || pendingVerificationEmail;

  useEffect(() => {
    if (!email && !isAuthenticated) {
      navigate("/login");
    }
  }, [email, isAuthenticated, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    dispatch(clearError());
    dispatch(clearSuccessMessage());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from;
      if (isNewUser) {
        navigate("/profile", { state: { from: from || "/" } });
      } else {
        if (from) {
          navigate(from, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    }
  }, [isAuthenticated, isNewUser, navigate, location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && otp) {
      dispatch(verifyEmail({ email, otp }));
    }
  };

  const handleResend = () => {
    if (email) {
      dispatch(resendOtp({ email }));
    }
  };

  const handleBack = () => {
    dispatch(setPendingVerificationEmail(null));
    navigate("/login");
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "radial-gradient(ellipse at 15% 10%, rgba(255, 155, 128, 0.12) 0%, transparent 45%), radial-gradient(ellipse at 85% 85%, rgba(255, 140, 56, 0.08) 0%, transparent 45%), #FAFAFA", display: "flex", flexDirection: "column" }}>
      <Box sx={{ py: { xs: 2, sm: 2.5 }, px: { xs: 2.5, sm: 4 }, width: "100%" }}>
        <img src={require("../../assets/images/handpik.png")} alt="Handpik" width={150} height={30} style={{ cursor: "pointer", display: "block" }} onClick={() => navigate("/")} />
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: { xs: "flex-start", sm: "flex-start" }, justifyContent: "center", px: { xs: 2, sm: 3 }, pb: { xs: 4, sm: 6 }, pt: { xs: 1, sm: 3 } }}>
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 480 }, position: "relative" }}>
          <IconButton onClick={handleBack} sx={{ position: { xs: "static", sm: "absolute" }, left: { sm: -56 }, top: { sm: 6 }, mb: { xs: 2, sm: 0 }, backgroundColor: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(6px)", border: "1px solid rgba(0, 0, 0, 0.08)", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", width: { xs: 42, sm: 40 }, height: { xs: 42, sm: 40 }, display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { backgroundColor: "#fff", transform: "scale(1.05)", boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }, transition: "all 0.2s ease" }}>
            <ArrowBackIosOutlined sx={{ fontSize: { xs: "18px", sm: "17px" }, color: "#1a1a1a", ml: "4px" }} />
          </IconButton>

          <Paper elevation={0} sx={{ width: "100%", borderRadius: { xs: "24px", sm: "28px" }, background: "rgba(255, 255, 255, 0.98)", backdropFilter: "blur(12px)", border: "1px solid rgba(0, 0, 0, 0.06)", boxShadow: { xs: "0 10px 35px -5px rgba(0, 0, 0, 0.07), 0 0 1px rgba(0,0,0,0.08)", sm: "0 20px 50px -10px rgba(0, 0, 0, 0.05), 0 0 1px rgba(0,0,0,0.08)" }, overflow: "hidden" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: { xs: 4.5, sm: 5 }, pb: { xs: 3, sm: 3.5 }, px: { xs: 3, sm: 5 }, background: "linear-gradient(180deg, rgba(255, 155, 128, 0.06) 0%, transparent 100%)" }}>
              <Box sx={{ width: { xs: 70, sm: 68 }, height: { xs: 70, sm: 68 }, borderRadius: "22px", bgcolor: "rgba(255, 140, 56, 0.08)", border: "1px solid rgba(255, 140, 56, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2, boxShadow: "0 8px 24px rgba(255, 140, 56, 0.10)" }}>
                <img src={require("../../assets/images/login-handpik-logo_cropped.png")} alt="Handpik" style={{ width: 40, height: 40, objectFit: "contain" }} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: { xs: 3, sm: 5 }, pt: { xs: 2, sm: 3 }, pb: { xs: 4.5, sm: 5 } }}>
              <Typography component="h1" sx={{ fontFamily: "'Lora', serif", fontSize: { xs: "25px", sm: "28px" }, fontWeight: 600, color: "#1A1A1A", letterSpacing: "-0.02em", textAlign: "center", mb: 1.2 }}>
                Verify Your Email
              </Typography>
              <Typography variant="body2" sx={{ color: "#71717A", fontSize: { xs: "14px", sm: "13.5px" }, textAlign: "center", lineHeight: 1.55, maxWidth: { xs: "100%", sm: "340px" }, mb: 3 }}>
                We've sent a 6-digit verification code to <br /> <b>{email}</b>
              </Typography>

              {error && <Alert severity="error" sx={{ width: "100%", maxWidth: "360px", mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>{error}</Alert>}
              {successMessage && <Alert severity="success" sx={{ width: "100%", maxWidth: "360px", mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>{successMessage}</Alert>}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: { xs: "100%", sm: "360px" }, display: "flex", flexDirection: "column", gap: 2, mb: 1 }}>
                <TextField label="6-Digit OTP" variant="outlined" fullWidth required value={otp} onChange={(e) => setOtp(e.target.value)} disabled={loading} inputProps={{ maxLength: 6 }} />
                <Button type="submit" variant="contained" fullWidth disabled={loading || otp.length !== 6} sx={{ py: 1.5, bgcolor: "#FF8C38", color: "white", fontWeight: 600, textTransform: "none", fontSize: "15px", borderRadius: "12px", boxShadow: "0 4px 14px rgba(255, 140, 56, 0.25)", "&:hover": { bgcolor: "#e67a2a" } }}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Email"}
                </Button>
                <Typography variant="caption" sx={{ color: "#A1A1AA", mt: 0.5, textAlign: "center", display: "block", alignSelf: "center", maxWidth: "280px" }}>
                  If you do not see the email in your inbox, please check your spam or junk folder.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#71717A", mt: 2, textAlign: "center" }}>
                Didn't receive the code?{" "}
                <span onClick={handleResend} style={{ color: "#FF8C38", textDecoration: "none", fontWeight: 600, cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1 }}>
                  Resend OTP
                </span>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
